import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type BadgeSize = "sm" | "md" | "lg";

const BADGE_SIZE: Record<BadgeSize, string> = {
  sm: "h-3.5 w-3.5 text-[8px]",
  md: "h-5 w-5 text-[11px]",
  lg: "h-7 w-7 text-sm",
};

export function FoundingMemberBadge({
  className,
  size = "md",
}: {
  className?: string;
  size?: BadgeSize;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-black text-white font-serif italic leading-none ring-2 ring-white shrink-0",
        BADGE_SIZE[size],
        className,
      )}
      aria-label="Founding member"
      title="Founding member"
    >
      f
    </span>
  );
}

export function FoundingMemberTag({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-black bg-black px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white",
        className,
      )}
    >
      <span className="font-serif italic lowercase normal-case tracking-normal text-xs leading-none">f</span>
      Founding
    </span>
  );
}

export function TestFlightTesterTag({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700",
        className,
      )}
    >
      TestFlight
    </span>
  );
}

export function UserAvatar({
  src,
  alt,
  fallback,
  fallbackClassName,
  className,
  isFoundingMember,
  badgeSize = "sm",
  badgeClassName,
}: {
  src?: string | null;
  alt: string;
  fallback: string;
  fallbackClassName?: string;
  className?: string;
  isFoundingMember?: boolean;
  badgeSize?: BadgeSize;
  badgeClassName?: string;
}) {
  return (
    <span className={cn("relative inline-flex shrink-0 rounded-full", className)}>
      <Avatar className={cn("h-full w-full", className)}>
        {src ? <AvatarImage src={src} alt={alt} className="object-cover" referrerPolicy="no-referrer" /> : null}
        <AvatarFallback className={fallbackClassName}>{fallback}</AvatarFallback>
      </Avatar>
      {isFoundingMember && (
        <FoundingMemberBadge
          size={badgeSize}
          className={cn(
            "absolute -bottom-0.5 z-10 left-auto -right-0.5",
            badgeClassName,
          )}
        />
      )}
    </span>
  );
}
